import React, {useEffect, useState} from 'react'
import styled, {keyframes} from 'styled-components'
import {isMobile} from '@/src/shared/utils/functions'

/* ─── Keyframes ─────────────────────────────────────── */

const pulse = keyframes`
  0%   { transform: scale(1);    opacity: 0.9; }
  50%  { transform: scale(1.08); opacity: 1;   }
  100% { transform: scale(1);    opacity: 0.9; }
`

const float = keyframes`
  0%,100% { transform: translateY(0px);  }
  50%      { transform: translateY(-8px); }
`

const drawArc = keyframes`
  from { stroke-dashoffset: 200; }
  to   { stroke-dashoffset: 0;   }
`

const slashIn = keyframes`
  from { stroke-dashoffset: 120; opacity: 0; }
  to   { stroke-dashoffset: 0;   opacity: 1; }
`

const slashPulse = keyframes`
  0%,100% { opacity: 1;   }
  50%      { opacity: 0.45; }
`

const dotPop = keyframes`
  0%   { r: 0;   opacity: 0; }
  60%  { r: 7;   opacity: 1; }
  80%  { r: 5.5;            }
  100% { r: 6;   opacity: 1; }
`

/* ─── Styled components ──────────────────────────────── */

const Wrapper = styled.div`
  position: fixed;
  left: 0;
  top: 0;
  min-height: 100vh;
  width: 100%;
  background: radial-gradient(ellipse at top, #1a1500 0%, #0d0a00 60%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  font-family: Inter, sans-serif;
  color: white;
  z-index: 5000;
`

const Card = styled.div`
  width: 100%;
  max-width: 480px;
  background: linear-gradient(
    160deg,
    rgba(255, 255, 255, 0.08) 0%,
    #ffcc0819 40%,
    rgba(255, 204, 8, 0.05) 100%
  );
  backdrop-filter: blur(18px);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-bottom-color: #ffcc0833;
  border-left-color: #ffcc0826;
  border-right-color: #ffcc0826;
  border-radius: 28px;
  padding: 48px 36px;
  text-align: center;
  box-shadow:
    0 20px 60px rgba(0, 0, 0, 0.5),
    inset 0 1px 0 rgba(255, 255, 255, 0.16);
`

/* Обёртка иконки — левитация + кольца */
const IconWrapper = styled.div`
  width: 140px;
  height: 140px;
  margin: 0 auto 28px;
  border-radius: 50%;
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.09) 0%, #2a1f00 35%, #1a1300 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${float} 4s ease-in-out infinite;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    inset: -5px;
    border-radius: 50%;
    border: 1px solid rgba(255, 255, 255, 0.1);
  }

  &::after {
    content: '';
    position: absolute;
    inset: -13px;
    border-radius: 50%;
    border: 2px solid #ffcc0866;
    animation: ${pulse} 2.5s infinite;
  }
`

/* SVG-дуги — анимация через css-prop */
const Arc = styled.path<{$delay: number}>`
  stroke-dasharray: 200;
  stroke-dashoffset: 200;
  animation: ${drawArc} 0.45s ${({$delay}) => $delay}s ease-out forwards;
`

const DotCircle = styled.circle`
  r: 0;
  opacity: 0;
  animation: ${dotPop} 0.4s 0.75s ease-out forwards;
`

const Slash = styled.line`
  stroke-dasharray: 120;
  stroke-dashoffset: 120;
  opacity: 0;
  animation:
    ${slashIn} 0.35s 0.95s ease-out forwards,
    ${slashPulse} 2.2s 1.5s ease-in-out infinite;
`

const Title = styled.h1`
  margin: 0;
  font-size: 36px;
  font-weight: 800;
  letter-spacing: -0.03em;
  color: #ffd500;
`

const Subtitle = styled.p`
  margin-top: 16px;
  color: rgba(255, 255, 255, 0.58);
  font-size: 16px;
  line-height: 1.7;
`

const Divider = styled.div`
  margin: 24px auto 0;
  width: 72%;
  height: 1px;
  background: linear-gradient(
    90deg,
    transparent 0%,
    rgba(255, 255, 255, 0.1) 25%,
    #ffcc0840 50%,
    rgba(255, 255, 255, 0.1) 75%,
    transparent 100%
  );
`

const RetryButton = styled.button`
  margin-top: 30px;
  border: 1px solid rgba(255, 255, 255, 0.22);
  outline: none;
  cursor: pointer;
  padding: 14px 32px;
  border-radius: 16px;
  background: linear-gradient(135deg, #ffd500 0%, #eabc0cff 100%);
  color: #1a1300;
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 0.01em;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.38);
  transition: all 0.25s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow:
      0 10px 28px #ffcc0855,
      inset 0 1px 0 rgba(255, 255, 255, 0.38);
    background: linear-gradient(135deg, #ffdd1a 0%, #ffd500 100%);
  }

  &:active {
    transform: scale(0.98);
  }
`

/* ─── Иконка Wi-Fi OFF ───────────────────────────────── */
function WifiOffIcon() {
  return (
    <svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Большая дуга */}
      <Arc
        $delay={0.1}
        d="M8,38 Q40,6 72,38"
        stroke="#FFD500"
        strokeWidth="5.5"
        strokeLinecap="round"
      />
      {/* Средняя дуга */}
      <Arc
        $delay={0.28}
        d="M20,50 Q40,30 60,50"
        stroke="#FFD500"
        strokeWidth="5.5"
        strokeLinecap="round"
      />
      {/* Малая дуга */}
      <Arc
        $delay={0.46}
        d="M32,62 Q40,54 48,62"
        stroke="#FFD500"
        strokeWidth="5.5"
        strokeLinecap="round"
      />
      {/* Точка */}
      <DotCircle cx="40" cy="73" fill="#FFD500" />

      {/* Перечёркивающая черта — белая */}
      <Slash
        x1="14"
        y1="70"
        x2="68"
        y2="14"
        stroke="rgba(255,255,255,0.92)"
        strokeWidth="4.5"
        strokeLinecap="round"
      />
    </svg>
  )
}

/* ─── Компонент ──────────────────────────────────────── */
export default function OfflinePage() {
  const [isOffline, setIsOffline] = useState(false)

  useEffect(() => {
    const handleOffline = () => setIsOffline(true)
    const handleOnline = () => setIsOffline(false)

    let removeNetworkListener: (() => void) | undefined

    if (isMobile()) {
      import('@capacitor/network').then(({Network}) => {
        Network.addListener('networkStatusChange', (status: {connected: boolean}) => {
          if (status.connected) {
            handleOnline()
          } else {
            handleOffline()
          }
        }).then(listener => {
          removeNetworkListener = listener.remove
        })
      })
    }

    window.addEventListener('offline', handleOffline)
    window.addEventListener('online', handleOnline)

    return () => {
      removeNetworkListener?.()
      window.removeEventListener('offline', handleOffline)
      window.removeEventListener('online', handleOnline)
    }
  }, [])

  if (!isOffline) return null

  return (
    <Wrapper>
      <Card>
        <IconWrapper>
          <WifiOffIcon />
        </IconWrapper>

        <Title>Ошибка соединения</Title>

        <Subtitle>
          Похоже, соединение с интернетом потеряно.
          <br />
          Проверьте подключение и попробуйте снова.
        </Subtitle>

        <Divider />

        <RetryButton onClick={() => window.location.reload()}>Попробовать снова</RetryButton>
      </Card>
    </Wrapper>
  )
}
